import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { CheckCircle, Circle, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL = { system: 'Onboarding', recruiter: 'Documents', driver: 'Your Actions' };

const OnboardingChecklistCard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/driver-onboarding/checklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setData(null); return; }
      setData(await res.json());
    } catch { setData(null); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (task) => {
    if (!task.actionable) return;
    setSaving(task.key);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/driver-onboarding/acknowledge`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_key: task.key, completed: !task.completed }),
      });
      if (res.ok) setData(await res.json());
    } finally { setSaving(null); }
  };

  if (loading) {
    return (
      <Card data-testid="onboarding-checklist-loading">
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null; // flag OFF or no application -> render nothing

  const groups = ['system', 'recruiter', 'driver'];

  return (
    <Card data-testid="onboarding-checklist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Onboarding Checklist
        </CardTitle>
        <CardDescription data-testid="onboarding-checklist-progress">
          {data.progress.completed} of {data.progress.total} steps complete
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {groups.map((g) => {
          const items = data.tasks.filter((t) => t.category === g);
          if (!items.length) return null;
          return (
            <div key={g}>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {CATEGORY_LABEL[g]}
              </p>
              <div className="space-y-2">
                {items.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    disabled={!t.actionable || saving === t.key}
                    onClick={() => toggle(t)}
                    data-testid={`onboarding-task-${t.key}`}
                    className={`w-full flex items-center gap-3 text-left rounded-lg px-3 py-2 ${
                      t.actionable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    {saving === t.key ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    ) : t.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : t.actionable ? (
                      <Circle className="w-5 h-5 text-gray-300" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-300" />
                    )}
                    <span className={`flex-1 text-sm ${t.completed ? 'text-gray-900' : 'text-gray-500'}`}>
                      {t.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklistCard;
